import { db } from "./dbClient";

// ─── ComplianceProgramBuilder.jsx ────────────────────────────────────────────
// "Build Your Compliance Program" — BSA/AML compliance program builder
// Full walkthrough of all required and optional compliance program components
// with step-by-step detail, action items, and AI assistant integration.

import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";

const PROXY = import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001";
const LS_KEY = "crossregguard_compliance_program";

// ─── Program Steps Data ───────────────────────────────────────────────────────
const PROGRAM_STEPS = [
  // ── FOUNDATION ──────────────────────────────────────────────────────────────
  {
    id: "governance",
    number: 1,
    category: "FOUNDATION",
    icon: "🏛",
    title: "Governance & Senior Management Oversight",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(h)",
      "FFIEC BSA/AML Manual — Board & Senior Management",
      "FinCEN Guidance FIN-2014-A007",
    ],
    summary:
      "A compliant BSA/AML program begins with active board and senior management engagement. The board must understand the institution's BSA/AML risks and approve the compliance program, while senior management is responsible for implementation.",
    whatItIncludes: [
      "Board-approved BSA/AML Policy",
      "Designated BSA/AML Compliance Officer with sufficient authority and resources",
      "Senior management oversight of BSA/AML risk",
      "Clear reporting lines from BSA officer to board",
      "Compliance committee or equivalent governance structure",
      "Annual BSA/AML program updates to the board",
    ],
    actionItems: [
      "Designate a BSA/AML Compliance Officer in writing",
      "Document board approval of BSA/AML policy",
      "Establish a compliance committee or governance body",
      "Define reporting structure for BSA officer",
      "Schedule annual board briefing on BSA/AML risks",
      "Document senior management's role in program oversight",
    ],
    keyQuestions: [
      "Has the board formally approved the BSA/AML program?",
      "Does the BSA officer have direct access to senior management?",
      "Are BSA/AML risks communicated to the board at least annually?",
    ],
  },
  {
    id: "risk_assessment",
    number: 2,
    category: "FOUNDATION",
    icon: "⚖",
    title: "BSA/AML Risk Assessment",
    required: true,
    regulatoryRefs: [
      "FFIEC BSA/AML Manual — Risk Assessment",
      "FinCEN Guidance on Risk Assessment",
      "Basel AML Index Methodology",
    ],
    summary:
      "An enterprise-wide BSA/AML risk assessment is the foundation of a risk-based compliance program. It identifies, measures, and documents the institution's exposure to money laundering and terrorist financing risks across products, services, customers, and geographies.",
    whatItIncludes: [
      "Enterprise-wide risk assessment methodology",
      "Product and service risk ratings",
      "Customer risk scoring criteria",
      "Geographic risk factors (FATF, high-risk jurisdictions)",
      "Delivery channel risk assessment",
      "Residual risk after controls",
      "Annual review and update process",
    ],
    actionItems: [
      "Document risk assessment methodology and scope",
      "Identify all products, services, and customer segments",
      "Rate each for ML/TF risk (Low/Medium/High)",
      "Map high-risk geographic exposure (FATF blacklist/greylist, OFAC)",
      "Calculate residual risk after existing controls",
      "Obtain senior management sign-off",
      "Schedule annual reassessment",
      "Link risk assessment results to program enhancements",
    ],
    keyQuestions: [
      "When was the risk assessment last updated?",
      "Does it cover all products, services, customers, and geographies?",
      "Are residual risks within the institution's risk appetite?",
    ],
  },
  // ── CORE PROGRAM ────────────────────────────────────────────────────────────
  {
    id: "policies",
    number: 3,
    category: "CORE PROGRAM",
    icon: "📋",
    title: "Policies & Procedures",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(h)",
      "12 CFR 21.21 (OCC)",
      "12 CFR 208.63 (Federal Reserve)",
      "FFIEC BSA/AML Manual",
    ],
    summary:
      "Written policies and procedures are the backbone of any BSA/AML compliance program. They provide employees with clear guidance on identifying and reporting suspicious activity, conducting customer due diligence, and meeting regulatory requirements.",
    whatItIncludes: [
      "AML/BSA Master Policy",
      "Customer Identification Program (CIP) Policy",
      "Customer Due Diligence (CDD) Policy",
      "Enhanced Due Diligence (EDD) Policy",
      "Suspicious Activity Report (SAR) Policy",
      "Currency Transaction Report (CTR) Policy",
      "OFAC/Sanctions Screening Policy",
      "Politically Exposed Person (PEP) Policy",
      "Correspondent Banking Policy",
      "Record Retention Policy",
      "Travel Rule / Wire Transfer Policy",
    ],
    actionItems: [
      "Inventory all existing BSA/AML policies",
      "Identify gaps against regulatory requirements",
      "Draft or update each required policy",
      "Establish annual policy review cycle",
      "Ensure policies are approved by appropriate authority",
      "Distribute policies to relevant staff",
      "Maintain version control and change log",
      "Document exceptions and waivers process",
    ],
    keyQuestions: [
      "Are all policies current and reviewed within the past 12 months?",
      "Do procedures align with the institution's actual practices?",
      "Is there a process for updating policies when regulations change?",
    ],
  },
  {
    id: "cip",
    number: 4,
    category: "CORE PROGRAM",
    icon: "🪪",
    title: "Customer Identification Program (CIP)",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(l)",
      "31 CFR 1020.220",
      "FinCEN CIP Rule",
      "USA PATRIOT Act § 326",
    ],
    summary:
      "The Customer Identification Program (CIP) requires financial institutions to verify the identity of customers at account opening. A robust CIP is the first line of defense against money laundering by ensuring you know who you are doing business with.",
    whatItIncludes: [
      "Minimum customer identification requirements (name, DOB, address, ID number)",
      "Identity verification methods (documentary and non-documentary)",
      "Beneficial ownership collection for legal entity customers",
      "Customer risk rating at onboarding",
      "CIP notice to customers",
      "Reliance on third-party CIP procedures",
      "Government list checks at account opening",
    ],
    actionItems: [
      "Document minimum information collected for each customer type",
      "Define acceptable identity verification documents",
      "Implement non-documentary verification for online accounts",
      "Establish beneficial ownership collection threshold (25%+)",
      "Create CIP notice/disclosure for customers",
      "Integrate government list screening at onboarding",
      "Define process for customers who fail CIP",
    ],
    keyQuestions: [
      "Are all required identity elements collected for every customer type?",
      "How is identity verification handled for online/digital onboarding?",
      "What happens when a customer cannot provide required information?",
    ],
  },
  {
    id: "cdd",
    number: 5,
    category: "CORE PROGRAM",
    icon: "🔍",
    title: "Customer Due Diligence (CDD) & Risk Rating",
    required: true,
    regulatoryRefs: [
      "31 CFR 1020.210",
      "FinCEN CDD Final Rule (May 2016)",
      "FFIEC BSA/AML Manual — CDD",
      "Basel Committee Guidelines",
    ],
    summary:
      "Customer Due Diligence (CDD) establishes a risk-based understanding of each customer's expected activity. The FinCEN CDD Final Rule requires financial institutions to identify and verify beneficial owners and understand the nature and purpose of customer relationships.",
    whatItIncludes: [
      "Customer risk rating methodology",
      "Standard CDD for all customers",
      "Nature and purpose of account/relationship documentation",
      "Ongoing monitoring requirements by risk tier",
      "Beneficial ownership for legal entity customers",
      "Periodic account reviews by risk tier",
      "CDD refresh triggers (material change, SAR filing, EDD completion)",
    ],
    actionItems: [
      "Define customer risk rating criteria and scoring model",
      "Document nature and purpose collection process",
      "Establish CDD refresh schedule by risk tier (High: 12 mo, Med: 24 mo, Low: 36 mo)",
      "Define CDD triggers requiring immediate refresh",
      "Implement beneficial ownership collection for entities",
      "Train staff on CDD requirements",
      "Document CDD records and retention",
    ],
    keyQuestions: [
      "Are customer risk ratings reviewed and updated regularly?",
      "Is there a documented process for collecting beneficial ownership?",
      "How are high-risk customers identified and escalated?",
    ],
  },
  {
    id: "edd",
    number: 6,
    category: "CORE PROGRAM",
    icon: "🔬",
    title: "Enhanced Due Diligence (EDD)",
    required: true,
    regulatoryRefs: [
      "31 CFR 1020.210(b)(5)",
      "FFIEC BSA/AML Manual — EDD",
      "FinCEN Guidance on High-Risk Customers",
      "FATF Recommendation 19",
    ],
    summary:
      "Enhanced Due Diligence (EDD) applies to customers or relationships that pose higher money laundering risks. EDD requires a deeper understanding of the customer's business, source of funds, and expected transaction activity.",
    whatItIncludes: [
      "EDD trigger criteria (PEPs, high-risk jurisdictions, cash-intensive businesses, MSBs)",
      "Senior management approval for high-risk relationships",
      "Source of wealth and source of funds documentation",
      "Enhanced ongoing monitoring procedures",
      "Regular EDD review cycle",
      "Correspondent bank EDD (Wolfsberg Questionnaire)",
      "Private banking EDD requirements",
    ],
    actionItems: [
      "Define all EDD trigger categories",
      "Document EDD procedures for each high-risk category",
      "Establish senior management approval process",
      "Create source of wealth/funds questionnaire",
      "Implement enhanced monitoring for EDD customers",
      "Define EDD review frequency (typically 12 months)",
      "Maintain EDD documentation and audit trail",
    ],
    keyQuestions: [
      "Is there a comprehensive list of EDD triggers?",
      "Does senior management approve all high-risk relationships?",
      "Are EDD reviews completed on schedule?",
    ],
  },
  {
    id: "beneficial_ownership",
    number: 7,
    category: "CORE PROGRAM",
    icon: "🏢",
    title: "Beneficial Ownership & Legal Entity Customers",
    required: true,
    regulatoryRefs: [
      "31 CFR 1010.230",
      "FinCEN CDD Final Rule",
      "FinCEN FAQ on Beneficial Ownership",
      "FATF Recommendation 24",
    ],
    summary:
      "The FinCEN Beneficial Ownership Rule requires covered financial institutions to collect beneficial ownership information from legal entity customers at account opening. This rule is designed to prevent the misuse of shell companies and legal entities for money laundering.",
    whatItIncludes: [
      "Identification of legal entity customers subject to the rule",
      "25% ownership threshold for beneficial owners",
      "Control prong (single individual with significant managerial control)",
      "Certification form collection and verification",
      "Exclusions and exemptions",
      "Reliance on existing customer information",
      "Annual or trigger-based refresh",
    ],
    actionItems: [
      "Identify all legal entity customer types subject to the rule",
      "Implement BO collection at account opening",
      "Create/adopt BO certification form",
      "Document verification procedures for BO information",
      "Define refresh triggers and schedule",
      "Train customer-facing staff",
      "Integrate BO data into ongoing monitoring",
    ],
    keyQuestions: [
      "Is beneficial ownership collected for all covered legal entity customers?",
      "Are control prong individuals identified in addition to 25%+ owners?",
      "What triggers a beneficial ownership refresh?",
    ],
  },
  {
    id: "transaction_monitoring",
    number: 8,
    category: "CORE PROGRAM",
    icon: "⇄",
    title: "Transaction Monitoring Program",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(g)",
      "FFIEC BSA/AML Manual — Transaction Monitoring",
      "FinCEN SAR Rule",
      "OCC Guidance on TM",
    ],
    summary:
      "A transaction monitoring program detects potentially suspicious activity by analyzing customer transactions against established patterns and rules. An effective program includes calibrated rules, alert review procedures, and escalation processes.",
    whatItIncludes: [
      "Transaction monitoring system (automated or manual)",
      "Detection scenario library and rule set",
      "Alert prioritization methodology",
      "Alert review and disposition procedures",
      "Escalation process to SAR decision",
      "System calibration and tuning documentation",
      "Look-back review process",
      "Third-party vendor management (if applicable)",
    ],
    actionItems: [
      "Document all active monitoring rules/scenarios",
      "Establish alert triage and review SLA (e.g., 30 days)",
      "Create alert disposition decision tree",
      "Define escalation criteria for SAR consideration",
      "Schedule quarterly rule calibration review",
      "Document look-back review triggers",
      "Maintain system validation records",
      "Train analysts on alert review procedures",
    ],
    keyQuestions: [
      "Are monitoring rules calibrated to the institution's risk profile?",
      "What is the current alert backlog and average resolution time?",
      "How often are monitoring rules reviewed and updated?",
    ],
  },
  // ── REPORTING ────────────────────────────────────────────────────────────────
  {
    id: "sar",
    number: 9,
    category: "REPORTING",
    icon: "◉",
    title: "Suspicious Activity Reporting (SAR)",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(g)",
      "31 CFR 1020.320",
      "FinCEN SAR Rule",
      "FFIEC BSA/AML Manual — SAR",
    ],
    summary:
      "Filing Suspicious Activity Reports (SARs) is a core BSA obligation. Financial institutions must file SARs when they know, suspect, or have reason to suspect that a transaction involves funds from illegal activity, is designed to evade reporting requirements, or lacks a lawful purpose.",
    whatItIncludes: [
      "SAR filing decision process and escalation",
      "SAR committee or review process",
      "Filing threshold ($5,000 for banks, $2,000 for MSBs)",
      "30-day filing deadline (60 days with extension)",
      "SAR quality review checklist",
      "Continuing activity SARs (90-day rule)",
      "SAR confidentiality requirements (no tipping off)",
      "Law enforcement voluntary information sharing",
    ],
    actionItems: [
      "Document SAR decision-making criteria and escalation path",
      "Establish SAR committee (or equivalent review process)",
      "Create SAR quality review checklist",
      "Define continuing activity SAR triggers (90-day rule)",
      "Train staff on SAR confidentiality",
      "Implement SAR tracking and deadline management",
      "Conduct SAR look-back as needed",
      "Maintain SAR records for 5 years",
    ],
    keyQuestions: [
      "Is there a documented SAR decision-making process?",
      "Are SARs filed within the required 30/60-day deadline?",
      "Are continuing activity SARs filed every 90 days?",
    ],
  },
  {
    id: "ctr",
    number: 10,
    category: "REPORTING",
    icon: "💵",
    title: "Currency Transaction Reporting (CTR)",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5313",
      "31 CFR 1010.311",
      "31 CFR 1010.312",
      "FinCEN CTR Rule",
    ],
    summary:
      "Currency Transaction Reports (CTRs) must be filed for cash transactions exceeding $10,000. Institutions must also identify structuring — the breaking up of cash transactions to avoid the CTR threshold — which is itself a federal crime.",
    whatItIncludes: [
      "$10,000 cash transaction threshold",
      "Aggregation rules for multiple same-day transactions",
      "CTR exemptions (Phase I and Phase II)",
      "Structuring detection and SAR filing",
      "15-day filing deadline",
      "CTR system and automation",
      "Exemption list maintenance",
    ],
    actionItems: [
      "Document CTR filing procedures and aggregation rules",
      "Implement CTR automation in core banking system",
      "Maintain CTR exemption list and annual review",
      "Define structuring detection parameters",
      "Train tellers and front-line staff",
      "Establish CTR amendment procedures",
      "Retain CTRs for 5 years",
    ],
    keyQuestions: [
      "Are CTRs filed within the 15-day deadline?",
      "Is the exemption list reviewed annually?",
      "How is structuring detected and reported?",
    ],
  },
  {
    id: "ofac",
    number: 11,
    category: "REPORTING",
    icon: "🛡",
    title: "OFAC / Sanctions Screening",
    required: true,
    regulatoryRefs: [
      "50 U.S.C. § 1701 et seq.",
      "31 CFR Chapter V",
      "OFAC Compliance Framework (2019)",
      "OFAC FAQs",
    ],
    summary:
      "OFAC sanctions compliance requires financial institutions to screen customers, transactions, and counterparties against OFAC's Specially Designated Nationals (SDN) and blocked persons lists, as well as country-based sanctions programs.",
    whatItIncludes: [
      "OFAC SDN list screening at onboarding and ongoing",
      "Transaction screening (wires, ACH, SWIFT)",
      "Country/jurisdiction screening program",
      "Match resolution procedures (true match vs. false positive)",
      "Blocking and rejection of transactions",
      "OFAC reporting requirements (10-day rule)",
      "Screening technology and frequency",
      "Designated OFAC compliance officer",
    ],
    actionItems: [
      "Implement automated OFAC screening at onboarding",
      "Screen all transactions above de minimis threshold",
      "Document match resolution decision tree",
      "Establish OFAC blocked property reporting (10-day rule)",
      "Define annual OFAC list update procedures",
      "Designate an OFAC compliance owner",
      "Conduct OFAC screening testing",
      "Document all OFAC decisions and dispositions",
    ],
    keyQuestions: [
      "How often are OFAC lists updated in the screening system?",
      "What is the process for resolving a potential OFAC match?",
      "Are wire transfers screened in real-time?",
    ],
  },
  // ── OVERSIGHT ────────────────────────────────────────────────────────────────
  {
    id: "independent_testing",
    number: 12,
    category: "OVERSIGHT",
    icon: "🔎",
    title: "Independent Testing & Model Validation",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(h)(2)",
      "FFIEC BSA/AML Manual — Independent Testing",
      "OCC Model Risk Management Guidance (OCC 2011-12)",
    ],
    summary:
      "Independent testing — also called audit — is one of the five pillars of an effective BSA/AML program. An independent third party or internal audit function must review the program at least annually to assess its effectiveness and identify gaps.",
    whatItIncludes: [
      "Annual independent BSA/AML program audit",
      "Scope covering all program elements",
      "Audit by qualified, independent party",
      "Transaction testing and sampling methodology",
      "Findings, recommendations, and management response",
      "Audit committee reporting",
      "Remediation tracking and follow-up",
      "Model validation for TM systems",
    ],
    actionItems: [
      "Schedule annual BSA/AML independent audit",
      "Define audit scope covering all five pillars",
      "Select qualified independent auditor (internal or external)",
      "Ensure auditor has access to all relevant records",
      "Develop management response to findings",
      "Track remediation to closure",
      "Report audit results to board/audit committee",
      "Validate TM models annually or after material changes",
    ],
    keyQuestions: [
      "When was the last independent BSA/AML audit completed?",
      "Were all prior findings fully remediated?",
      "Is the auditor truly independent of the BSA function?",
    ],
  },
  {
    id: "training",
    number: 13,
    category: "OVERSIGHT",
    icon: "📚",
    title: "BSA/AML Training Program",
    required: true,
    regulatoryRefs: [
      "31 U.S.C. § 5318(h)(3)",
      "FFIEC BSA/AML Manual — Training",
      "FinCEN Guidance on Training",
    ],
    summary:
      "All employees whose job functions touch BSA/AML obligations must receive appropriate training. Training should be commensurate with each employee's role, cover relevant regulatory requirements, and be updated when laws or procedures change.",
    whatItIncludes: [
      "Annual BSA/AML training for all relevant staff",
      "Role-specific training (tellers, relationship managers, compliance staff)",
      "New hire orientation training",
      "BSA officer advanced training",
      "Board and senior management training",
      "Training tracking and completion records",
      "Training content review and updates",
      "Third-party training resources",
    ],
    actionItems: [
      "Map training requirements to job roles",
      "Develop or source annual AML training curriculum",
      "Create new hire BSA/AML onboarding training",
      "Design advanced training for BSA/compliance staff",
      "Prepare board-level BSA training materials",
      "Implement training tracking and attestation system",
      "Define training completion deadlines and escalation",
      "Update training materials annually",
    ],
    keyQuestions: [
      "Has 100% of required staff completed annual training?",
      "Is training tailored to specific job roles?",
      "Are training records retained for at least 5 years?",
    ],
  },
  {
    id: "recordkeeping",
    number: 14,
    category: "OVERSIGHT",
    icon: "📁",
    title: "Record Retention",
    required: true,
    regulatoryRefs: [
      "31 CFR 1010.430",
      "31 U.S.C. § 5314",
      "12 CFR 12 (OCC)",
      "FFIEC BSA/AML Manual — Recordkeeping",
    ],
    summary:
      "BSA requires financial institutions to retain certain records for specified periods. Proper recordkeeping ensures the institution can produce records for law enforcement, regulatory examinations, and internal investigations.",
    whatItIncludes: [
      "5-year retention for SARs, CTRs, CIP records, and transaction records",
      "Wire transfer records (Recordkeeping Rule, Travel Rule)",
      "Monetary instrument sales records over $3,000",
      "Purchase of cashier's checks, money orders",
      "Funds transfer records (transmittals of funds)",
      "Data integrity and secure storage",
      "Retrieval procedures for law enforcement requests",
    ],
    actionItems: [
      "Map all BSA recordkeeping requirements to retention schedule",
      "Implement 5-year retention for all BSA records",
      "Establish wire transfer recordkeeping system",
      "Document monetary instrument logs",
      "Define secure storage and access controls",
      "Test record retrieval capability",
      "Train staff on recordkeeping requirements",
    ],
    keyQuestions: [
      "Are all required records retained for the full 5-year period?",
      "Can required records be produced promptly for law enforcement?",
      "Is there a retention schedule that covers all BSA record types?",
    ],
  },
  {
    id: "correspondent_banking",
    number: 15,
    category: "OVERSIGHT",
    icon: "⇌",
    title: "Correspondent Banking & Wire Transfer Compliance",
    required: false,
    regulatoryRefs: [
      "31 CFR 1010.610",
      "FinCEN Travel Rule (31 CFR 1010.410(f))",
      "Wolfsberg Correspondent Banking Principles",
      "FATF Recommendation 13",
    ],
    summary:
      "Correspondent banking relationships and wire transfer services carry significant money laundering risks. Financial institutions must conduct EDD on foreign correspondent banks and comply with the Travel Rule for transmittals of funds.",
    whatItIncludes: [
      "EDD for foreign correspondent bank relationships",
      "Wolfsberg Questionnaire (or equivalent)",
      "Prohibition on shell bank relationships",
      "SWIFT / payment message monitoring",
      "Travel Rule compliance ($3,000 threshold)",
      "Nested correspondent relationship controls",
      "Payable-through account controls",
    ],
    actionItems: [
      "Inventory all correspondent banking relationships",
      "Obtain Wolfsberg Questionnaires for all foreign correspondents",
      "Implement Travel Rule procedures ($3,000 threshold)",
      "Prohibit relationships with shell banks",
      "Screen correspondent banks at onboarding and ongoing",
      "Monitor correspondent transaction activity",
      "Define payable-through account controls",
    ],
    keyQuestions: [
      "Are Travel Rule obligations met for all qualifying transmittals?",
      "Have all foreign correspondents provided Wolfsberg Questionnaires?",
      "Are nested correspondent risks identified and controlled?",
    ],
  },
  {
    id: "virtual_assets",
    number: 16,
    category: "OVERSIGHT",
    icon: "₿",
    title: "Virtual Assets & Cryptocurrency",
    required: false,
    regulatoryRefs: [
      "FinCEN Guidance FIN-2019-G001",
      "FATF Guidance on Virtual Assets (2021)",
      "31 CFR 1010.100(ff)",
      "FinCEN Rule on Unhosted Wallets (proposed)",
    ],
    summary:
      "As virtual assets become mainstream in financial services, institutions must assess their exposure to crypto-related ML/TF risks and implement appropriate controls, especially if serving virtual asset service providers (VASPs) or allowing crypto-related transactions.",
    whatItIncludes: [
      "Virtual asset risk assessment and policy",
      "VASP customer due diligence",
      "Blockchain analytics / transaction monitoring tools",
      "Travel Rule compliance for virtual asset transfers",
      "Unhosted wallet controls",
      "Crypto-asset sanctioned address screening",
      "VASP licensing verification",
    ],
    actionItems: [
      "Conduct virtual asset risk assessment",
      "Define acceptable VASP customer criteria",
      "Implement blockchain analytics tool for crypto TM",
      "Document Travel Rule procedures for virtual asset transfers",
      "Screen crypto wallets against OFAC/sanctioned lists",
      "Verify VASP licensing in jurisdiction of incorporation",
      "Update training to cover virtual asset risks",
    ],
    keyQuestions: [
      "Has the institution assessed its exposure to virtual asset risks?",
      "Are VASP customers subject to EDD?",
      "Is blockchain analytics used for transaction monitoring?",
    ],
  },
];

const CATEGORIES = ["FOUNDATION", "CORE PROGRAM", "REPORTING", "OVERSIGHT"];

// ─── localStorage helpers ─────────────────────────────────────────────────────
function loadProgress() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { completedSteps: [], actionItemsDone: {}, customNotes: {}, customTitles: {}, questionAnswers: {}, lastUpdated: null };
    const parsed = JSON.parse(raw);
    if (!parsed.questionAnswers) parsed.questionAnswers = {};
    return parsed;
  } catch {
    return { completedSteps: [], actionItemsDone: {}, customNotes: {}, customTitles: {}, questionAnswers: {}, lastUpdated: null };
  }
}

function saveProgress(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ ...data, lastUpdated: new Date().toISOString().slice(0, 10) }));
  } catch {}
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "default", disabled, small, style: extraStyle = {} }) {
  const C = useTheme();
  const base = {
    padding: small ? "5px 12px" : "8px 18px",
    borderRadius: 7,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: small ? 11 : 13,
    fontFamily: "'DM Mono',monospace",
    fontWeight: 600,
    transition: "opacity 0.15s",
    opacity: disabled ? 0.5 : 1,
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
  const variants = {
    default: { background: C.accentGlow, color: C.accent, border: `1px solid ${C.accent}40` },
    danger:  { background: `${C.danger}15`, color: C.danger, border: `1px solid ${C.danger}40` },
    success: { background: `${C.success}15`, color: C.success, border: `1px solid ${C.success}40` },
    ghost:   { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` },
    solid:   { background: C.accent, color: C.bg, border: "none" },
    warn:    { background: `${C.warn}15`, color: C.warn, border: `1px solid ${C.warn}40` },
    purple:  { background: `${C.purple || "#7C3AED"}15`, color: C.purple || "#7C3AED", border: `1px solid ${(C.purple || "#7C3AED")}40` },
  };
  return (
    <button style={{ ...base, ...variants[variant], ...extraStyle }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ComplianceProgramBuilder({ currentUser, company }) {
  const C = useTheme();
  const { t } = useTranslation();

  // ── State ──────────────────────────────────────────────────────────────────
  const [progress, setProgress] = useState(loadProgress);
  const [selectedStep, setSelectedStep] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Edit state
  const [editTitle, setEditTitle] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // ── E-035 / F-002: Load progress from Supabase (authoritative) on mount ───
  useEffect(() => {
    if (!company?.id) return;
    db.from("compliance_program_progress")
      .select("progress_data")
      .eq("company_id", company.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('[ComplianceProgram] Load error:', error.message);
          return;
        }
        if (data?.progress_data) {
          setProgress(prev => ({ ...prev, ...data.progress_data }));
          saveProgress(data.progress_data);
        }
      })
      .catch(err => console.error('[ComplianceProgram] Load failed:', err.message));
  }, [company?.id]);

  // ── Persist progress to localStorage + Supabase ────────────────────────────
  useEffect(() => {
    saveProgress(progress);
    if (!company?.id || !currentUser?.id) return;
    db.upsert("compliance_program_progress", {
      company_id:    company.id,
      progress_data: progress,
      updated_by:    currentUser.id,
    }).then(({ error }) => {
      if (error) console.error('[ComplianceProgram] Save error:', error.message);
    }).catch(err => console.error('[ComplianceProgram] Save failed:', err.message));
  }, [progress, company?.id, currentUser?.id]);

  // ── Scroll chat to bottom ──────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // ── When step changes in edit mode, sync edit fields ─────────────────────
  useEffect(() => {
    if (selectedStep) {
      setEditTitle(progress.customTitles?.[selectedStep.id] || selectedStep.title);
      setEditNotes(progress.customNotes?.[selectedStep.id] || "");
    }
  }, [selectedStep?.id, editMode]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const totalSteps = PROGRAM_STEPS.length;
  const completedCount = progress.completedSteps?.length || 0;
  const progressPct = Math.round((completedCount / totalSteps) * 100);

  // E-036: Compliance maturity score — required steps weighted 2x optional
  const maturityScore = (() => {
    const requiredSteps = PROGRAM_STEPS.filter(s => s.required);
    const optionalSteps = PROGRAM_STEPS.filter(s => !s.required);
    const requiredDone  = requiredSteps.filter(s => (progress.completedSteps || []).includes(s.id)).length;
    const optionalDone  = optionalSteps.filter(s => (progress.completedSteps || []).includes(s.id)).length;
    const maxScore = requiredSteps.length * 2 + optionalSteps.length;
    const rawScore = requiredDone * 2 + optionalDone;
    const pct = maxScore > 0 ? Math.round((rawScore / maxScore) * 100) : 0;
    const level = pct >= 80 ? "Advanced" : pct >= 60 ? "Intermediate" : pct >= 40 ? "Developing" : pct >= 20 ? "Initial" : "Not Started";
    const color = pct >= 80 ? "#276749" : pct >= 60 ? "#2A69AC" : pct >= 40 ? "#744210" : pct >= 20 ? "#C53030" : "#97266D";
    return { pct, level, color, requiredDone, requiredTotal: requiredSteps.length, optionalDone, optionalTotal: optionalSteps.length };
  })();

  function isCompleted(stepId) {
    return (progress.completedSteps || []).includes(stepId);
  }

  function isActionDone(stepId, idx) {
    return (progress.actionItemsDone?.[stepId] || []).includes(String(idx));
  }

  function toggleStepComplete(stepId) {
    setProgress(prev => {
      const completed = prev.completedSteps || [];
      const next = completed.includes(stepId)
        ? completed.filter(id => id !== stepId)
        : [...completed, stepId];
      return { ...prev, completedSteps: next };
    });
  }

  function toggleActionItem(stepId, idx) {
    setProgress(prev => {
      const done = prev.actionItemsDone?.[stepId] || [];
      const key = String(idx);
      const next = done.includes(key) ? done.filter(k => k !== key) : [...done, key];
      return { ...prev, actionItemsDone: { ...(prev.actionItemsDone || {}), [stepId]: next } };
    });
  }

  function saveEditFields(stepId) {
    setProgress(prev => ({
      ...prev,
      customTitles: { ...(prev.customTitles || {}), [stepId]: editTitle },
      customNotes: { ...(prev.customNotes || {}), [stepId]: editNotes },
    }));
  }

  function saveQuestionAnswer(stepId, qIdx, answer) {
    setProgress(prev => {
      const stepAnswers = { ...(prev.questionAnswers?.[stepId] || {}) };
      stepAnswers[String(qIdx)] = answer;
      return { ...prev, questionAnswers: { ...(prev.questionAnswers || {}), [stepId]: stepAnswers } };
    });
  }

  function getQuestionAnswer(stepId, qIdx) {
    return progress.questionAnswers?.[stepId]?.[String(qIdx)] || "";
  }

  function countAnsweredQuestions(stepId, total) {
    const answers = progress.questionAnswers?.[stepId] || {};
    return Object.values(answers).filter(a => a.trim()).length;
  }

  function getStepTitle(step) {
    return progress.customTitles?.[step.id] || step.title;
  }

  // ── AI Chat ────────────────────────────────────────────────────────────────
  function buildSystemPrompt() {
    let base =
      "You are a BSA/AML compliance expert helping a Chief Compliance Officer build their compliance program. " +
      "You have deep knowledge of the Bank Secrecy Act, FinCEN regulations, FFIEC examination procedures, " +
      "FATF recommendations, and best practices for financial institution compliance programs.";
    if (selectedStep) {
      base += `\n\nThe user is currently viewing Step ${selectedStep.number}: "${getStepTitle(selectedStep)}". ` +
        `Regulatory references: ${selectedStep.regulatoryRefs.join(", ")}. ` +
        `Summary: ${selectedStep.summary}`;
    }
    return base;
  }

  async function sendChat(messageText) {
    const text = messageText || chatInput.trim();
    if (!text || chatLoading) return;
    setChatInput("");
    const userMsg = { role: "user", content: text };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatLoading(true);
    try {
      const res = await fetch(`${PROXY}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          messages: newMessages,
          max_tokens: 1500,
          system: buildSystemPrompt(),
        }),
      });
      const data = await res.json();
      const reply = data?.content?.[0]?.text || data?.choices?.[0]?.message?.content || "";
      setChatMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: "assistant", content: `Error: ${err.message}` }]);
    } finally {
      setChatLoading(false);
    }
  }

  const SUGGESTED_PROMPTS = [
    "What are the FinCEN requirements for this step?",
    "Help me draft a policy for this section",
    "What are common exam findings for this area?",
    "How does FATF guidance apply here?",
  ];

  // ─── Colors & styles ────────────────────────────────────────────────────────
  const inputStyle = {
    width: "100%",
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    color: C.text,
    padding: "8px 10px",
    fontSize: 13,
    fontFamily: "'DM Mono',monospace",
    boxSizing: "border-box",
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: C.bg,
      color: C.text,
      fontFamily: "'DM Mono',monospace",
      overflow: "hidden",
    }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: C.surface,
        borderBottom: `1px solid ${C.border}`,
        padding: "16px 28px",
        display: "flex",
        alignItems: "center",
        gap: 20,
        flexShrink: 0,
        flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            fontFamily: "'Syne',sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: C.text,
            marginBottom: 2,
          }}>
            Build Your Compliance Program
          </div>
          <div style={{ color: C.textDim, fontSize: 11 }}>
            BSA/AML program builder — {company?.name || "Your Institution"} &nbsp;·&nbsp; {completedCount} of {totalSteps} steps complete
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ flex: 1, minWidth: 180, maxWidth: 300 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
            <span style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace" }}>PROGRAM PROGRESS</span>
            <span style={{ fontSize: 10, color: C.accent, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>{progressPct}%</span>
          </div>
          <div style={{ height: 6, background: C.surfaceAlt || C.bg, borderRadius: 4, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${progressPct}%`,
              background: progressPct >= 100 ? C.success : C.accent,
              borderRadius: 4,
              transition: "width 0.4s ease",
            }} />
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Btn
            variant={chatOpen ? "solid" : "default"}
            onClick={() => setChatOpen(o => !o)}
            small
          >
            ✦ AI Assistant
          </Btn>
          <Btn
            variant={editMode ? "warn" : "ghost"}
            onClick={() => {
              if (editMode && selectedStep) saveEditFields(selectedStep.id);
              setEditMode(m => !m);
            }}
            small
          >
            {editMode ? "✓ Done Editing" : "✎ Edit Program"}
          </Btn>
          <Btn
            variant="ghost"
            onClick={() => window.print()}
            small
          >
            ⬇ Export PDF
          </Btn>
        </div>
      </div>

      {/* ── Body: sidebar + content ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>

        {/* ── Left Sidebar: Step List ─────────────────────────────────────── */}
        <div style={{
          width: 340,
          minWidth: 280,
          background: C.surface,
          borderRight: `1px solid ${C.border}`,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflow: "hidden",
        }}>
          {/* Sidebar progress summary */}
          <div style={{
            padding: "14px 16px",
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}>
            {/* E-036: Maturity score badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, padding: "8px 12px", background: `${maturityScore.color}12`, border: `1px solid ${maturityScore.color}35`, borderRadius: 7 }}>
              <div style={{ textAlign: "center", minWidth: 44 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: maturityScore.color, fontFamily: "'Syne',sans-serif", lineHeight: 1 }}>{maturityScore.pct}%</div>
                <div style={{ fontSize: 9, color: maturityScore.color, letterSpacing: "0.05em", marginTop: 1 }}>MATURITY</div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: maturityScore.color }}>{maturityScore.level}</div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>
                  {maturityScore.requiredDone}/{maturityScore.requiredTotal} required · {maturityScore.optionalDone}/{maturityScore.optionalTotal} optional
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: C.textMid, fontFamily: "'DM Mono',monospace" }}>
                {completedCount} / {totalSteps} STEPS COMPLETE
              </span>
              <span style={{
                fontSize: 10,
                fontFamily: "'DM Mono',monospace",
                fontWeight: 700,
                color: progressPct >= 100 ? C.success : C.accent,
              }}>{progressPct}%</span>
            </div>
            <div style={{ height: 4, background: C.bg, borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${progressPct}%`,
                background: progressPct >= 100 ? C.success : C.accent,
                borderRadius: 3,
                transition: "width 0.4s ease",
              }} />
            </div>
          </div>

          {/* Step list scrollable */}
          <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
            {CATEGORIES.map(cat => {
              const stepsInCat = PROGRAM_STEPS.filter(s => s.category === cat);
              const completedInCat = stepsInCat.filter(s => isCompleted(s.id)).length;
              return (
                <div key={cat}>
                  {/* Category header */}
                  <div style={{
                    padding: "10px 16px 6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                    <span style={{
                      fontSize: 9,
                      fontFamily: "'DM Mono',monospace",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.textDim,
                    }}>{cat}</span>
                    <span style={{
                      fontSize: 9,
                      fontFamily: "'DM Mono',monospace",
                      color: completedInCat === stepsInCat.length ? C.success : C.textDim,
                    }}>{completedInCat}/{stepsInCat.length}</span>
                  </div>

                  {stepsInCat.map(step => {
                    const done = isCompleted(step.id);
                    const active = selectedStep?.id === step.id;
                    const actionsDone = (progress.actionItemsDone?.[step.id] || []).length;
                    const actionsTotal = step.actionItems.length;
                    return (
                      <button
                        key={step.id}
                        onClick={() => setSelectedStep(step)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          padding: "9px 16px",
                          background: active ? C.accentGlow : "transparent",
                          border: "none",
                          borderLeft: active ? `3px solid ${C.accent}` : "3px solid transparent",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.12s",
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          onClick={e => { e.stopPropagation(); toggleStepComplete(step.id); }}
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: `2px solid ${done ? C.success : C.border}`,
                            background: done ? C.success : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: 1,
                            cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          {done && <span style={{ color: C.bg, fontSize: 10, lineHeight: 1 }}>✓</span>}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <span style={{ fontSize: 12, color: C.textDim, fontFamily: "'DM Mono',monospace" }}>
                              {String(step.number).padStart(2, "0")}
                            </span>
                            <span style={{ fontSize: 13 }}>{step.icon}</span>
                            <span style={{
                              fontSize: 11,
                              fontFamily: "'DM Mono',monospace",
                              color: active ? C.accent : done ? C.success : C.text,
                              fontWeight: active ? 700 : 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            }}>
                              {getStepTitle(step)}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {step.required ? (
                              <span style={{
                                fontSize: 9,
                                fontFamily: "'DM Mono',monospace",
                                color: C.danger,
                                background: `${C.danger}15`,
                                padding: "1px 5px",
                                borderRadius: 3,
                                fontWeight: 700,
                              }}>REQUIRED</span>
                            ) : (
                              <span style={{
                                fontSize: 9,
                                fontFamily: "'DM Mono',monospace",
                                color: C.textDim,
                                background: `${C.textDim}15`,
                                padding: "1px 5px",
                                borderRadius: 3,
                              }}>OPTIONAL</span>
                            )}
                            {actionsDone > 0 && (
                              <span style={{
                                fontSize: 9,
                                color: actionsDone === actionsTotal ? C.success : C.accent,
                                fontFamily: "'DM Mono',monospace",
                              }}>
                                {actionsDone}/{actionsTotal} tasks
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right Panel: Step Detail ─────────────────────────────────────── */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "28px 36px",
          transition: "margin-right 0.3s ease",
          marginRight: chatOpen ? 400 : 0,
        }}>
          {!selectedStep ? (
            // Welcome screen
            <WelcomeScreen C={C} totalSteps={totalSteps} completedCount={completedCount} progressPct={progressPct} />
          ) : (
            // Step detail
            <StepDetail
              step={selectedStep}
              C={C}
              progress={progress}
              editMode={editMode}
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              inputStyle={inputStyle}
              isCompleted={isCompleted}
              isActionDone={isActionDone}
              toggleStepComplete={toggleStepComplete}
              toggleActionItem={toggleActionItem}
              saveEditFields={saveEditFields}
              getStepTitle={getStepTitle}
              saveQuestionAnswer={saveQuestionAnswer}
              getQuestionAnswer={getQuestionAnswer}
            />
          )}
        </div>

        {/* ── AI Chat Sidebar ──────────────────────────────────────────────── */}
        <div style={{
          position: "fixed",
          top: 0,
          right: chatOpen ? 0 : -400,
          width: 400,
          height: "100vh",
          background: C.surface,
          borderLeft: `1px solid ${C.border}`,
          display: "flex",
          flexDirection: "column",
          zIndex: 200,
          transition: "right 0.3s ease",
          boxShadow: chatOpen ? "-4px 0 24px rgba(0,0,0,0.3)" : "none",
        }}>
          {/* Chat header */}
          <div style={{
            padding: "16px 18px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: C.text }}>
                ✦ AI BSA/AML Assistant
              </div>
              <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
                {selectedStep ? `Context: Step ${selectedStep.number} — ${getStepTitle(selectedStep)}` : "Ask anything about your compliance program"}
              </div>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 16, padding: 4 }}
            >✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
            {chatMessages.length === 0 && (
              <div>
                <div style={{ color: C.textMid, fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
                  I'm your BSA/AML compliance expert. Ask me anything about building your compliance program, regulatory requirements, or exam preparation.
                </div>
                <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8, fontFamily: "'DM Mono',monospace", letterSpacing: "0.08em" }}>
                  SUGGESTED PROMPTS
                </div>
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendChat(prompt)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      background: C.accentGlow,
                      border: `1px solid ${C.accent}30`,
                      borderRadius: 7,
                      padding: "8px 12px",
                      marginBottom: 7,
                      color: C.textMid,
                      fontSize: 11,
                      fontFamily: "'DM Mono',monospace",
                      cursor: "pointer",
                      lineHeight: 1.4,
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{
                  fontSize: 9,
                  fontFamily: "'DM Mono',monospace",
                  color: msg.role === "user" ? C.accent : C.purple || "#7C3AED",
                  marginBottom: 4,
                  letterSpacing: "0.08em",
                }}>
                  {msg.role === "user" ? "YOU" : "AI ASSISTANT"}
                </div>
                <div style={{
                  background: msg.role === "user" ? C.accentGlow : `${C.purple || "#7C3AED"}10`,
                  border: `1px solid ${msg.role === "user" ? C.accent + "30" : (C.purple || "#7C3AED") + "25"}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontSize: 12,
                  color: C.text,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: "flex", gap: 5, padding: "10px 0" }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: C.accent,
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                    opacity: 0.6,
                  }} />
                ))}
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat input */}
          <div style={{
            padding: "12px 14px",
            borderTop: `1px solid ${C.border}`,
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", gap: 8 }}>
              <textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); }
                }}
                placeholder="Ask about BSA/AML requirements… (Enter to send)"
                rows={2}
                style={{
                  flex: 1,
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 7,
                  color: C.text,
                  padding: "8px 10px",
                  fontSize: 12,
                  fontFamily: "'DM Mono',monospace",
                  resize: "none",
                  outline: "none",
                }}
              />
              <button
                onClick={() => sendChat()}
                disabled={!chatInput.trim() || chatLoading}
                style={{
                  background: C.accent,
                  border: "none",
                  borderRadius: 7,
                  color: C.bg,
                  padding: "8px 14px",
                  cursor: !chatInput.trim() || chatLoading ? "not-allowed" : "pointer",
                  fontSize: 14,
                  opacity: !chatInput.trim() || chatLoading ? 0.5 : 1,
                  flexShrink: 0,
                }}
              >↑</button>
            </div>
            {chatMessages.length > 0 && (
              <button
                onClick={() => setChatMessages([])}
                style={{
                  marginTop: 6,
                  background: "transparent",
                  border: "none",
                  color: C.textDim,
                  fontSize: 10,
                  fontFamily: "'DM Mono',monospace",
                  cursor: "pointer",
                  padding: 0,
                }}
              >Clear conversation</button>
            )}
          </div>
        </div>
      </div>

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.4; }
          40% { transform: scale(1.1); opacity: 1; }
        }
        @media print {
          body * { visibility: hidden; }
          #compliance-print-area, #compliance-print-area * { visibility: visible; }
          #compliance-print-area { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}

// ─── Welcome Screen ───────────────────────────────────────────────────────────
function WelcomeScreen({ C, totalSteps, completedCount, progressPct }) {
  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{
        fontFamily: "'Syne',sans-serif",
        fontWeight: 700,
        fontSize: 26,
        color: C.text,
        marginBottom: 10,
      }}>
        Build Your BSA/AML Compliance Program
      </div>
      <div style={{ color: C.textMid, fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
        This tool guides you through all {totalSteps} components of a complete Bank Secrecy Act / Anti-Money Laundering
        compliance program. Select a step from the left panel to view detailed requirements, regulatory references,
        action items, and key questions your examiners will ask.
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        {[
          { label: "Total Steps", value: totalSteps, color: C.accent },
          { label: "Completed", value: completedCount, color: C.success },
          { label: "Remaining", value: totalSteps - completedCount, color: C.warn },
          { label: "Progress", value: `${progressPct}%`, color: C.accent },
        ].map(stat => (
          <div key={stat.label} style={{
            background: C.surfaceAlt || C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "14px 20px",
            minWidth: 100,
          }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 22, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace", marginTop: 2 }}>{stat.label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Five Pillars */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "20px 24px",
        marginBottom: 24,
      }}>
        <div style={{
          fontFamily: "'Syne',sans-serif",
          fontWeight: 700,
          fontSize: 14,
          color: C.text,
          marginBottom: 14,
        }}>
          The Five Pillars of an Effective BSA/AML Program
        </div>
        {[
          { icon: "🏛", pillar: "Internal Controls", desc: "Policies, procedures, and systems to identify, manage, and mitigate BSA/AML risks" },
          { icon: "🪪", pillar: "Compliance Officer", desc: "Designated BSA/AML officer with sufficient authority, resources, and expertise" },
          { icon: "📚", pillar: "Training", desc: "Ongoing, role-specific training for all personnel with BSA/AML responsibilities" },
          { icon: "🔎", pillar: "Independent Testing", desc: "Annual audit of the BSA/AML program by a qualified, independent party" },
          { icon: "🔍", pillar: "Customer Due Diligence", desc: "Risk-based CDD and ongoing monitoring of customer relationships" },
        ].map((p, i) => (
          <div key={i} style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: i < 4 ? 12 : 0,
            paddingBottom: i < 4 ? 12 : 0,
            borderBottom: i < 4 ? `1px solid ${C.border}` : "none",
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{p.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 2 }}>{p.pillar}</div>
              <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.5 }}>{p.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
        Select any step from the left panel to get started. Use the AI Assistant for real-time guidance on specific requirements.
      </div>
    </div>
  );
}

// ─── Question Card (answerable) ───────────────────────────────────────────────
function QuestionCard({ question, qIdx, stepId, savedAnswer, hasAnswer, C, onSave }) {
  const [draft, setDraft] = React.useState(savedAnswer);
  const [saved, setSaved] = React.useState(false);

  // Sync draft when step changes
  React.useEffect(() => { setDraft(savedAnswer); setSaved(false); }, [stepId, qIdx, savedAnswer]);

  function handleSave() {
    onSave(stepId, qIdx, draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const isDirty = draft !== savedAnswer;

  return (
    <div style={{
      background: hasAnswer ? `${C.success}08` : `${C.warn}08`,
      border: `1px solid ${hasAnswer ? C.success + "30" : C.warn + "25"}`,
      borderRadius: 10,
      overflow: "hidden",
    }}>
      {/* Question row */}
      <div style={{
        padding: "10px 14px",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        borderBottom: `1px solid ${hasAnswer ? C.success + "20" : C.warn + "15"}`,
      }}>
        <span style={{
          color: hasAnswer ? C.success : C.warn,
          fontSize: 13, flexShrink: 0, marginTop: 1, fontWeight: 700,
        }}>
          {hasAnswer ? "✓" : "?"}
        </span>
        <span style={{
          fontSize: 12, color: C.text,
          fontFamily: "'DM Mono',monospace", lineHeight: 1.6, fontWeight: 600,
        }}>{question}</span>
      </div>
      {/* Answer area */}
      <div style={{ padding: "10px 14px" }}>
        {hasAnswer && !isDirty ? (
          /* Saved answer display */
          <div style={{ marginBottom: 8 }}>
            <div style={{
              fontSize: 10, color: C.success, letterSpacing: "0.08em",
              textTransform: "uppercase", marginBottom: 5, fontFamily: "'DM Mono',monospace",
            }}>Your Answer</div>
            <div style={{
              fontSize: 12, color: C.textMid, lineHeight: 1.7,
              whiteSpace: "pre-wrap", fontFamily: "'DM Mono',monospace",
            }}>{savedAnswer}</div>
          </div>
        ) : null}
        <textarea
          value={draft}
          onChange={e => { setDraft(e.target.value); setSaved(false); }}
          placeholder="Type your answer here…"
          rows={hasAnswer && !isDirty ? 2 : 3}
          style={{
            width: "100%", boxSizing: "border-box",
            background: "rgba(0,0,0,0.15)", border: `1px solid ${C.border}`,
            borderRadius: 6, color: C.text, fontSize: 12, padding: "8px 10px",
            fontFamily: "'DM Mono',monospace", outline: "none", resize: "vertical",
            lineHeight: 1.6,
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, gap: 8, alignItems: "center" }}>
          {saved && (
            <span style={{ color: C.success, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
              ✓ Saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!draft.trim() || !isDirty}
            style={{
              padding: "5px 14px", borderRadius: 6, fontSize: 11,
              fontFamily: "'DM Mono',monospace", fontWeight: 600, cursor: "pointer",
              background: draft.trim() && isDirty ? C.accent : "transparent",
              border: `1px solid ${draft.trim() && isDirty ? C.accent : C.border}`,
              color: draft.trim() && isDirty ? C.bg : C.textDim,
              opacity: !draft.trim() ? 0.5 : 1,
              transition: "all 0.15s",
            }}
          >
            {hasAnswer && !isDirty ? "Edit" : "Save Answer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step Detail ──────────────────────────────────────────────────────────────
function StepDetail({
  step, C, progress, editMode,
  editTitle, setEditTitle, editNotes, setEditNotes,
  inputStyle, isCompleted, isActionDone,
  toggleStepComplete, toggleActionItem, saveEditFields, getStepTitle,
  saveQuestionAnswer, getQuestionAnswer,
}) {
  const done = isCompleted(step.id);
  const actionsDone = (progress.actionItemsDone?.[step.id] || []).length;
  const actionsTotal = step.actionItems.length;

  const SectionHeading = ({ children }) => (
    <div style={{
      fontFamily: "'Syne',sans-serif",
      fontWeight: 700,
      fontSize: 13,
      color: C.text,
      marginBottom: 12,
      marginTop: 24,
      paddingBottom: 6,
      borderBottom: `1px solid ${C.border}`,
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}>
      {children}
    </div>
  );

  return (
    <div style={{ maxWidth: 780 }} id="compliance-print-area">
      {/* Step header */}
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        marginBottom: 20,
      }}>
        {/* Step number badge */}
        <div style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: done ? `${C.success}20` : C.accentGlow,
          border: `2px solid ${done ? C.success : C.accent}40`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: "'Syne',sans-serif",
            fontWeight: 700,
            fontSize: 15,
            color: done ? C.success : C.accent,
          }}>{String(step.number).padStart(2, "0")}</span>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20 }}>{step.icon}</span>
            {editMode ? (
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                onBlur={() => saveEditFields(step.id)}
                style={{
                  ...inputStyle,
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  color: C.text,
                  flex: 1,
                }}
              />
            ) : (
              <h1 style={{
                fontFamily: "'Syne',sans-serif",
                fontWeight: 700,
                fontSize: 20,
                color: C.text,
                margin: 0,
              }}>{getStepTitle(step)}</h1>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {step.required ? (
              <span style={{
                fontSize: 10,
                fontFamily: "'DM Mono',monospace",
                fontWeight: 700,
                color: C.danger,
                background: `${C.danger}18`,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${C.danger}30`,
              }}>● REQUIRED</span>
            ) : (
              <span style={{
                fontSize: 10,
                fontFamily: "'DM Mono',monospace",
                color: C.textDim,
                background: `${C.textDim}18`,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${C.border}`,
              }}>OPTIONAL</span>
            )}
            <span style={{
              fontSize: 10,
              fontFamily: "'DM Mono',monospace",
              color: C.textDim,
            }}>
              {step.category}
            </span>
            {done && (
              <span style={{
                fontSize: 10,
                fontFamily: "'DM Mono',monospace",
                color: C.success,
                background: `${C.success}15`,
                padding: "3px 8px",
                borderRadius: 4,
              }}>✓ COMPLETE</span>
            )}
            {actionsDone > 0 && (
              <span style={{
                fontSize: 10,
                fontFamily: "'DM Mono',monospace",
                color: actionsDone === actionsTotal ? C.success : C.accent,
              }}>
                {actionsDone}/{actionsTotal} action items done
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Regulatory references */}
      <div style={{
        background: `${C.accent}08`,
        border: `1px solid ${C.accent}25`,
        borderRadius: 8,
        padding: "12px 16px",
        marginBottom: 6,
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}>
        <span style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace", marginRight: 4, letterSpacing: "0.08em", flexShrink: 0 }}>
          REGULATORY REFS:
        </span>
        {step.regulatoryRefs.map((ref, i) => (
          <span key={i} style={{
            fontSize: 10,
            fontFamily: "'DM Mono',monospace",
            color: C.accent,
            background: C.accentGlow,
            padding: "3px 8px",
            borderRadius: 4,
            border: `1px solid ${C.accent}30`,
          }}>{ref}</span>
        ))}
      </div>

      {/* Summary */}
      <div style={{
        fontSize: 13,
        color: C.textMid,
        lineHeight: 1.7,
        marginTop: 16,
        marginBottom: 4,
      }}>
        {step.summary}
      </div>

      {/* What It Includes */}
      <SectionHeading>📌 What It Includes</SectionHeading>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {step.whatItIncludes.map((item, i) => (
          <li key={i} style={{
            fontSize: 12,
            color: C.textMid,
            lineHeight: 1.7,
            marginBottom: 4,
            fontFamily: "'DM Mono',monospace",
          }}>{item}</li>
        ))}
      </ul>

      {/* Action Items */}
      <SectionHeading>✅ Action Items</SectionHeading>
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        overflow: "hidden",
      }}>
        {step.actionItems.map((item, i) => {
          const done = isActionDone(step.id, i);
          return (
            <div
              key={i}
              onClick={() => toggleActionItem(step.id, i)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "11px 16px",
                borderBottom: i < step.actionItems.length - 1 ? `1px solid ${C.border}` : "none",
                cursor: "pointer",
                background: done ? `${C.success}07` : "transparent",
                transition: "background 0.12s",
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                border: `2px solid ${done ? C.success : C.border}`,
                background: done ? C.success : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: 1,
                transition: "all 0.15s",
              }}>
                {done && <span style={{ color: C.bg, fontSize: 10, lineHeight: 1 }}>✓</span>}
              </div>
              <span style={{
                fontSize: 12,
                color: done ? C.textDim : C.text,
                fontFamily: "'DM Mono',monospace",
                lineHeight: 1.5,
                textDecoration: done ? "line-through" : "none",
                flex: 1,
              }}>{item}</span>
            </div>
          );
        })}
      </div>

      {/* Key Questions */}
      <SectionHeading>❓ Key Questions to Answer</SectionHeading>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {step.keyQuestions.map((q, i) => {
          const saved = getQuestionAnswer(step.id, i);
          const hasAnswer = saved.trim().length > 0;
          return (
            <QuestionCard
              key={i}
              question={q}
              qIdx={i}
              stepId={step.id}
              savedAnswer={saved}
              hasAnswer={hasAnswer}
              C={C}
              onSave={saveQuestionAnswer}
            />
          );
        })}
      </div>

      {/* Custom notes in edit mode */}
      {editMode && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 10,
            fontFamily: "'DM Mono',monospace",
            color: C.textDim,
            marginBottom: 8,
            letterSpacing: "0.08em",
          }}>CUSTOM NOTES (EDIT MODE)</div>
          <textarea
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
            onBlur={() => saveEditFields(step.id)}
            rows={4}
            placeholder="Add notes, internal context, or implementation details for this step..."
            style={{
              ...inputStyle,
              resize: "vertical",
              lineHeight: 1.6,
            }}
          />
        </div>
      )}

      {/* Show saved notes when not in edit mode */}
      {!editMode && progress.customNotes?.[step.id] && (
        <div style={{ marginTop: 20 }}>
          <div style={{
            fontSize: 10,
            fontFamily: "'DM Mono',monospace",
            color: C.textDim,
            marginBottom: 8,
            letterSpacing: "0.08em",
          }}>INTERNAL NOTES</div>
          <div style={{
            background: `${C.purple || "#7C3AED"}08`,
            border: `1px solid ${(C.purple || "#7C3AED")}25`,
            borderRadius: 8,
            padding: "12px 16px",
            fontSize: 12,
            color: C.textMid,
            fontFamily: "'DM Mono',monospace",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
          }}>
            {progress.customNotes[step.id]}
          </div>
        </div>
      )}

      {/* Mark Complete button */}
      <div style={{
        marginTop: 28,
        paddingTop: 20,
        borderTop: `1px solid ${C.border}`,
        display: "flex",
        gap: 10,
        alignItems: "center",
      }}>
        <button
          onClick={() => toggleStepComplete(step.id)}
          style={{
            padding: "10px 22px",
            borderRadius: 8,
            border: `1px solid ${isCompleted(step.id) ? C.border : C.success + "60"}`,
            background: isCompleted(step.id) ? "transparent" : `${C.success}15`,
            color: isCompleted(step.id) ? C.textDim : C.success,
            fontSize: 13,
            fontFamily: "'DM Mono',monospace",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {isCompleted(step.id) ? (
            <><span>✓ Completed</span><span style={{ fontSize: 10, opacity: 0.7 }}>— click to unmark</span></>
          ) : (
            <><span>◎ Mark as Complete</span></>
          )}
        </button>
        {actionsDone < actionsTotal && (
          <span style={{ fontSize: 11, color: C.textDim, fontFamily: "'DM Mono',monospace" }}>
            {actionsTotal - actionsDone} action item{actionsTotal - actionsDone !== 1 ? "s" : ""} remaining
          </span>
        )}
      </div>
    </div>
  );
}
